import type { CreateRewardPayload } from "@/modules/loyalty/domain/types";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormRoot, FormInput, FormNumber, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowLeftIcon, ImageIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { loyaltyApi } from "../api/client";
import {
  REWARD_IMAGE_KEY_PREFIX,
  RewardImageUploadField,
} from "./RewardImageUploadField";

type VariantOptionItem = { id: string; displayName: string };

const RewardFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ລາງວັນ"),
  requiredPoints: z.number().int().min(1, "ຕ້ອງມີຢ່າງນ້ອຍ 1 ແຕ້ມ"),
  variantRewardId: z.union([z.string(), z.null()]),
  imageUrl: z.string().nullable().optional(),
  isActive: z.boolean(),
});

export type RewardFormValues = z.infer<typeof RewardFormSchema>;

type RewardFormProps = {
  defaultValues?: Partial<RewardFormValues>;
  onSubmit: (values: CreateRewardPayload) => void | Promise<void>;
  submitting?: boolean;
  onCancel: () => void;
  submitLabel: string;
};

export function RewardForm({
  defaultValues,
  onSubmit,
  submitting,
  onCancel,
  submitLabel,
}: RewardFormProps) {
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const methods = useForm<RewardFormValues>({
    resolver: zodResolver(RewardFormSchema),
    defaultValues: {
      name: "",
      requiredPoints: 100,
      variantRewardId: null,
      imageUrl: null,
      isActive: true,
      ...defaultValues,
    },
  });

  const handleSubmit = async (values: RewardFormValues) => {
    let imageUrl = values.imageUrl?.trim() || null;
    if (pendingImageFile) {
      imageUrl = await uploadImageToKey(
        REWARD_IMAGE_KEY_PREFIX,
        pendingImageFile,
      );
      methods.setValue("imageUrl", imageUrl);
      setPendingImageFile(null);
    }

    await onSubmit({
      name: values.name.trim(),
      requiredPoints: values.requiredPoints,
      variantRewardId:
        values.variantRewardId && values.variantRewardId !== ""
          ? values.variantRewardId
          : null,
      imageUrl,
      isActive: values.isActive,
    });
  };

  return (
    <FormRoot<RewardFormValues> methods={methods} onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div className="rounded-xl border p-4">
          <div className="mb-3 flex items-center gap-2">
            <ImageIcon className="size-5 text-muted-foreground" />
            <span className="font-medium text-sm">ຮູບລາງວັນ</span>
          </div>
          <p className="text-muted-foreground mb-3 text-xs">
            ແສດງໃນແອັບລູກຄ້າ (ແຄັດຕາລັອກແລກລາງວັນ) — ອັດຕາສ່ວນ 1:1
          </p>
          <RHF.Controller
            name="imageUrl"
            control={methods.control}
            render={({ field }) => (
              <RewardImageUploadField
                value={field.value ?? ""}
                onChange={field.onChange}
                pendingFile={pendingImageFile}
                onFileSelect={setPendingImageFile}
                disabled={submitting}
              />
            )}
          />
        </div>

        <FormInput
          name="name"
          label="ຊື່ລາງວັນ"
          placeholder="ຕົວຢ່າງ: ເຄື່ອດດື່ມຟຣີ 1 ແກ້ວ"
          requiredMark
        />
        <FormNumber
          name="requiredPoints"
          label="ຈຳນວນແຕ້ມທີ່ໃຊ້ແລກ"
          placeholder="ຕົວຢ່າງ: 100"
          requiredMark
        />

        <FormInfiniteCombobox<VariantOptionItem>
          name="variantRewardId"
          label="ສິນຄ້າໃຫ້ເປັນລາງວັນ (ຖ້າມີ)"
          queryKey={["loyalty", "rewards", "variant-options"]}
          queryFn={({ search, pageParam }) =>
            loyaltyApi.listVariantOptionsLookup({
              search: search || undefined,
              page: pageParam,
              limit: 20,
            })
          }
          preloadQueryFn={(id) => loyaltyApi.getVariantOption(id)}
          getLabel={(item) => item.displayName}
          getValue={(item) => item.id}
          placeholder="ຄົ້ນຫາຫຼືເລືອກສິນຄ້າ..."
          clearable
        />
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">ເປີດໃຊ້ງານ</span>
          <RHF.Controller
            name="isActive"
            control={methods.control}
            render={({ field }) => (
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        </div>
      </div>

      <div className="mt-6 flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeftIcon className="size-4" />
          ຍົກເລີກ
        </Button>
      </div>
    </FormRoot>
  );
}
